import React, { Fragment } from "react";
import { Box, Typography, Paper, Stack } from "@mui/material";
import { ArrowOutward } from "@mui/icons-material";
import { mergeSx, Sx } from "utils/sx";
import { OptimizedImage } from "components/elements/OptimizedImage";
import { useRandomAd } from "components/routes/events/id/useRandomAd";
import { useAdminPageContext } from "components/layout/page/AdminPageProvider";
import { trackAdClick, trackAdImpression } from "utils/tracking";
import { useOrganisation } from "components/providers/OrganisationProvider";
import { useOnElementEnterViewport } from "hooks/useOnElementEnterViewport";
import { splitAtLastWord } from "utils/text";

const linkStyles: Sx = {
    display: "block",
};

const containerStyle: Sx = (theme) => ({
    display: "flex",
    position: "relative",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
});

const textStyles: Sx = (theme) => ({
    gap: theme.spacing(0.5),
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
});

const logoStyles: Sx = (theme) => ({
    width: theme.spacing(4),
    height: theme.spacing(4),
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
});

const tagStyles: Sx = (theme) => ({
    flexShrink: 0,
    alignSelf: "flex-start",
    backgroundColor: theme.palette.state.action,
    paddingX: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
});

const ctaNonBreakingSpanStyles: Sx = {
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
};

export const InlineAd: React.FC<{ placement: string, sx?: Sx, Wrapper?: React.FCWithChildren }> = ({ placement, sx, Wrapper }) => {
    const ad = useRandomAd();
    const org = useOrganisation();
    const ref = useOnElementEnterViewport(ad ? trackAdImpression(placement, org.id, ad.id) : () => {});
    const { isAdminPage } = useAdminPageContext() || {};

    if (!ad || isAdminPage) return null;

    const [breakingCta, nonBreakingCta] = splitAtLastWord(ad.callToAction);

    const WrapperComponent = Wrapper || Fragment;

    return (
        <WrapperComponent>
            <Box component="a" ref={ref} href={ad.url} target="_blank" rel="noopener noreferrer" sx={mergeSx(sx, linkStyles)} onClick={trackAdClick(placement, org.id, ad.id)}>
                <Paper elevation={0} sx={mergeSx(containerStyle, { bgcolor: ad.bgColor })} role="presentation">
                    <Box sx={logoStyles}>
                        <OptimizedImage src={ad.logo} alt="Logo" width={32} height={32} />
                    </Box>
                    <Stack flexGrow={1}>
                        <Stack sx={textStyles}>
                            <Box color={ad.callToActionColor} lineHeight={2.6} data-testid="cta-container">
                                <Typography variant="body2" fontWeight={600}>
                                    {breakingCta}{" "}
                                    <Box component="span" sx={ctaNonBreakingSpanStyles}>
                                        {nonBreakingCta}
                                        <ArrowOutward sx={{ verticalAlign: "middle", fontSize: "large" }} />
                                    </Box>
                                </Typography>
                            </Box>
                            {ad.tag && (
                                <Typography variant="label2" component="div" sx={tagStyles}>
                                    {ad.tag}
                                </Typography>
                            )}
                        </Stack>
                        <Typography variant="body2" fontWeight={400}>
                            {ad.copy}
                        </Typography>
                    </Stack>
                </Paper>
            </Box>
        </WrapperComponent>
    );
};
